import * as Sentry from '@sentry/react';
import { cfaSignIn, cfaSignOut } from 'capacitor-firebase-auth/alternative';
import { addDays, startOfDay } from 'date-fns';
import firebase from 'firebase/app';
import { createContext, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FullscreenLoader } from '../app/components/FullscreenLoader/FullscreenLoader';
import { shouldUseLoadingPerf } from '../app/utils/loading-strategy';
import * as sharded from '../app/utils/sharded-counter';
import {
  addConfidence,
  calculateDeviceIdentifier,
  canAccessFeature,
  clearWatchingInfo,
  CloudFunctions,
  Experiments,
  fn,
  formReducer,
  isItalian,
  manualCloudFunction,
  MINUTES,
  stableSort,
} from '../app/utils/utils';
import {
  analytics,
  appleAuthProvider,
  auth,
  database,
  emailAuthProvider,
  fieldValue,
  googleAuthProvider,
  realtimeDatabase,
  storage,
  timestamp,
  videoDataStorage,
} from '../firebase';
import { useCapacitor } from '../hooks/useCapacitor';
import { useCurrentLanguage } from '../hooks/useCurrentLanguage';
import { useSession } from '../hooks/useSession';
import { getLegacyMyListItems, mapRecommendationsByRequestedIds } from './firebase-loading-perf.helpers';
import { hasVerifiedCompanyAccess, isFreeWeekActive, shouldLockVideoForUser } from './free-week.helpers';
import { GenericObject } from './GamingContext';
import { useGeolocation } from './GeolocationContext';

const FirebaseContext = createContext<IFirebaseContext>(null);

type DocumentData = firebase.firestore.DocumentData;
type DocumentSnapshot = firebase.firestore.DocumentSnapshot<DocumentData>;
type UserCredential = firebase.auth.UserCredential;
type User = firebase.User;

interface IFirebaseContext {
  currentUser: User;
  isLoggedIn: boolean;
  userClaims: Record<string, any>;

  getStaticData: () => Promise<DocumentData>;

  userData: DocumentSnapshot;
  userDataFmt: any;
  userCompanyData: any;
  updateUserData: (data?: DocumentData, options?: { skipListUpdate?: boolean }) => Promise<void>;
  fadSubscribeToCourses: (courses: any[], fuid: string, fad_id: string, currentCreditBalance: number) => Promise<any>;
  getVideoShardedCounterIncrementor: (videoId: string) => () => Promise<void>;
  getVideoOpeningsShardedCounterIncrementor: (videoId: string) => () => Promise<void>;
  updateUserVideoAnalytics: (videoId: string, data: DocumentData) => Promise<void>;
  updateHubspotProperties: (data: { [p: string]: any }) => Promise<void>;
  getBrevoContactLink: (data: { uid: string; email?: string | null }) => Promise<{ url: string | null }>;

  plans: any[];
  promoCodes: any[];
  subscription: any;
  isSubscriptionCheckedOnce: boolean;
  globalSecondsIncrementor: () => void;

  isTrial: boolean;
  trialTimeLeft: number;
  isTrialExpired: boolean;

  getDeviceIdentifier: () => Promise<string>;
  checkDeviceRegistrationAbility: (deviceId: string) => Promise<{ deviceId: string; canRegister: boolean; existing?: boolean }>;
  registerDevice: (deviceInfo: { browser: string; os: string; deviceId: string }, existing?: boolean) => Promise<string>;
  getRegisteredDevices: () => Promise<DocumentData[]>;
  removeDevice: (deviceId: string) => Promise<void>;

  addCheckoutSession: (
    price: string,
    promotion_code?: string,
    to_ecm?: boolean,
    from_shop?: boolean,
    is_package?: boolean
  ) => Promise<{ sessionId: string; url: string }>;
  getCustomerPortalUrl: (isCustomFlow?: boolean) => Promise<any>;

  staticData: DocumentData;
  isFreeWeek: boolean;
  signupData: DocumentData;
  seoUpdateInProgress: boolean;
  seoUpdateLogs: any[];

  notifications: { title?: string; body: string; image?: string; read: boolean; data?: any }[];
  notificationBadge: number;
  addNotifications: (items: any[]) => void;
  readNotification: (index: number) => void;
  cleanNotifications: () => void;

  getVideosForTag: (tagId: string) => Promise<DocumentData[]>;
  requestVideoData: (videoId: string, drmType: string) => Promise<DocumentData>;
  videos: DocumentData[];
  comingSoonVideos: DocumentData[];
  mostViewsVideos: DocumentData[];
  freeVideos: DocumentData[];
  keepWatching: DocumentData[];
  newVideos: DocumentData[];
  series: DocumentData[];
  seriesAuthors: { author: DocumentData; authorVideos: DocumentData[] }[];
  seriesTags: { tag: DocumentData; tagVideos: DocumentData[] }[];
  hasMoreSeriesTags: boolean;
  loadMoreSeriesTags: () => Promise<void>;
  loadingMoreSeriesTags: boolean;
  courses: DocumentData[];
  billboard: DocumentData;
  getCourse: (id: string) => Promise<DocumentData>;
  getTest: (courseId: string, id: string) => Promise<DocumentData>;
  validateTest: (data: any) => Promise<any>;
  tags: DocumentData[];
  authors: DocumentData[];
  getSeries: (seriesId: string) => DocumentData;
  isVideoLocked: (video: any) => boolean;
  recommendations: DocumentData[];
  bonusVideos: DocumentData[];
  setValidBonus: (bonusDocumentId: string) => void;

  queryVideosByString: (query: string) => Promise<{ data: DocumentData[]; time: number }>;
  getVideoCardsByIds: (ids: string[]) => Promise<{ items: DocumentData[] }>;
  getCachedVideoCardsByIds: (ids: string[]) => DocumentData[];

  tagCards: { id: string; label: string }[];
  myList: DocumentData[];
  addToMyList: (videoId: string) => Promise<void>;
  removeFromMyList: (videoId: string) => Promise<void>;
  myListLoading: boolean;

  login: (email: string, password: string) => Promise<UserCredential>;
  loginWithGoogle: () => Promise<any>;
  loginWithApple: () => Promise<any>;
  loginWithToken: (token: string) => Promise<UserCredential>;
  signup: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateEmail: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  checkEmail: (email: string) => Promise<string[]>;
  sendEmailVerification: (path?: string) => Promise<void>;
  recoverPassword: (email: string) => Promise<void>;
  changePassword: (password: string) => Promise<void>;
  reauthenticate: (password: string) => Promise<UserCredential>;
  reauthenticateGoogle: () => Promise<UserCredential>;
  deleteAccount: () => Promise<void>;

  mfaGetSession: () => Promise<firebase.auth.MultiFactorSession>;
  mfaEnroll: (assertion: firebase.auth.MultiFactorAssertion) => Promise<void>;
  mfaUnenroll: (factorId: string) => Promise<void>;

  getActyTutorCategories: () => Promise<DocumentData[]>;
  getActyTutors: (startDate: Date, categoryId: string) => Promise<any>;
  getActyAppointments: (
    asAdmin?: boolean,
    page?: number,
    sorting?: { direction: 'asc' | 'desc'; field: string },
    filters?: any,
    exportCsv?: boolean
  ) => Promise<any[]>;
  getActyAppointment: (appointmentId: string) => Promise<DocumentData>;
  getActyReservationUrl: (actyTutorId: string, actyCategoryId: string) => Promise<any>;

  getTvLoginCode: () => Promise<string>;
  listenForTvLoginToken: (code: string) => Promise<string>;
  consumeTvLoginCode: (code: string) => Promise<boolean>;

  getTickets: (asAdmin?: boolean) => Promise<DocumentData[]>;
  getTicket: (ticketId: string, asAdmin?: boolean) => Promise<DocumentData>;
  submitTicket: (data: any) => Promise<{ ticketId: string; messageId: string }>;
  getTicketStatuses: () => Promise<DocumentData[]>;
  rateTicket: (ticketId: string, rating?: 1 | -1) => Promise<void>;
  assignTicket: (ticketId: string, assignee?: string) => Promise<void>;

  getUserData: (userIdentifier: { uid?: string; email?: string; alias?: any }) => Promise<DocumentData>;
  getUserCourse: (uid: string, cid: string, action: string, payload?: any) => Promise<any>;
  updateUser: (uid: string, updater: { [p: string]: any }) => Promise<boolean>;
  requestImpersonationToken: (uid: string, sender: string) => Promise<string>;

  getPublishData: () => Promise<{ tags: DocumentData[]; authors: DocumentData[]; series: DocumentData[] }>;
  requestVideoContent: (id: string) => Promise<DocumentData>;
  getStats: () => Promise<{ [p: string]: any }>;
  getVideoReports: () => Promise<{ reports: DocumentData[] }>;
  getFeedbacks: () => Promise<{ feedbacks: DocumentData[] }>;
  getLogs: (offset: number, elementId?: string) => Promise<DocumentData[]>;
  upsertHubspotProduct: (id: string, objectType: 'video' | 'tag') => Promise<boolean>;
  getStripeOneOffs: () => Promise<{ products: any[]; history: DocumentData[] }>;
  getCheckoutDetails: (id: string) => Promise<DocumentData>;
  createOneOffSession: (data: any) => Promise<any>;
  updateCustomerSubscription: (
    priceId?: string,
    upgradeInterval?: boolean,
    finalize?: string,
    cancel?: string,
    toECM?: boolean,
    isPreviewMode?: boolean,
    productToUpgradeToId?: string
  ) => Promise<boolean | DocumentData>;
  getCustomerData: () => Promise<any>;
  requestCollaboration: (request: any) => Promise<boolean>;
  getAdventCalendar: () => Promise<any>;
  unlockAdventDay: (day: number, year: number) => Promise<any>;
  notifyAdventData: (day: number, year: number, data?: any) => Promise<any>;
  trackConversion: (event: string, params?: { [p: string]: any }) => Promise<void>;
  getExperiments: () => Promise<DocumentData[]>;
  getAwsGuid: (guid: string) => Promise<any>;
  getEbooks: (page: number, isMobile: boolean) => Promise<{ list: DocumentData[]; hasMore: boolean }>;
  getNextQuestion: (questionId?: string, answerId?: string) => Promise<DocumentData>;
  unlockEbook: (ebookId: string, value: { questionId: string; answerId: string }[]) => Promise<{ success: boolean }>;
  getEbookDownload: (ebookId: string) => Promise<{ success: boolean; url: string }>;
  manageCache: (op: string, params?: any) => () => Promise<any>;
  getCourses: (companyId: string) => Promise<void>;
  getFadCourses: (uid?: string) => Promise<any>;
  getEcmPackages: (uid?: string) => Promise<any>;
  updateCourse: (
    name: string,
    withCertificate: boolean,
    visibleForAso: boolean,
    description: string[],
    visible: boolean,
    order: number,
    maxErrors: number,
    companyId?: string,
    id?: string
  ) => Promise<boolean>;
  requestCourseContent: (id: string) => Promise<DocumentData>;
  queryGoogleDrive: (
    op: 'LISTALL' | 'TOS3',
    options?: Partial<{
      pageToken: string;
      folder: string;
      fileId: string;
      fileName: string;
    }>
  ) => Promise<any>;
  queryHubspot: (op: 'BLOG_PAGE' | 'BLOG_ARTICLE' | 'BLOG_TAGS', options?: Partial<{ after: string; id: string }>) => Promise<any>;
  requestTicketRating: (ticketId: string, ticketTitle: string, authorId: string) => Promise<boolean>;
  getUserNotifications: (page: number) => Promise<DocumentData[]>;
  readAllUserNotifications: () => Promise<void>;
  sendVideoNotifications: (notificationId: string) => Promise<void>;
  sendNotification: (uids: string[], title: string, body: string, dryRun: boolean) => Promise<any>;
  getStripeDashboard: () => Promise<any>;
  submitReservation: (event: string, body: any) => Promise<any>;
  earlyRenew: (product?: any) => Promise<any>;
  buyShop: (priceId: string) => Promise<any>;
  getStreamingData: (channelId?: string) => Promise<any>;
  getCompanies: () => Promise<DocumentData[]>;
  getCompanyUsers: (companyId: string, page: number, filter?: string) => Promise<DocumentData[]>;
  updateCompany: (
    {
      enabled,
      companyData,
      users,
      userToDelete,
      deleteCompany,
    }: {
      enabled?: boolean;
      companyData?: GenericObject;
      users?: { email: string; nome: string; cognome: string; password: string }[];
      userToDelete?: string;
      deleteCompany?: boolean;
    },
    companyId?: string
  ) => Promise<{
    duplicated: string[];
    nonExistent: string[];
    occupied: { email: string; companyName: string }[];
    created: { email: string; uid: string; password: string }[];
    deleteErrors: any[];
  }>;

  getCustomFlowUsers: () => Promise<any>;
  massiveCustomSignup: (users: any[]) => Promise<any>;
  getCourseStatus: (user: string, courseIds: string[]) => Promise<any>;
  handleCustomUser: (uid: string, { stripeProductId, courseSlugs }: { stripeProductId?: string; courseSlugs?: string[] }) => Promise<any>;

  setTrack: any;
  setSocketFire: any;

  getLegal: (doc: 'privacy' | 'cookie' | 'tos' | 'bal' | 'contacts', year?: number) => Promise<{ content: string }>;
  getActyFaq: () => Promise<{ content: string }>;

  // remoteConfig: firebase.remoteConfig.RemoteConfig;
  storage: firebase.storage.Reference;
  videoDataStorage: firebase.storage.Reference;
  database: firebase.firestore.Firestore;
  realtimeDatabase: firebase.database.Database;
  analytics: firebase.analytics.Analytics;

  fv: typeof firebase.firestore.FieldValue;
  timestamp: typeof firebase.firestore.Timestamp;

  deviceFcmToken: string;
}

export const useFirebase = () => {
  return useContext<IFirebaseContext>(FirebaseContext);
};

export const FirebaseProvider = ({ children }) => {
  const { isNative } = useCapacitor();
  const { currentLanguage, supportedLanguages, updateLanguage } = useCurrentLanguage();
  const { country_code } = useGeolocation();
  const history = useHistory();

  const [loading, setLoading] = useState(true);

  const [currentUser, setCurrentUser] = useSession<User>('user', null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userClaims, setUserClaims] = useState<Record<string, any>>({});

  const [userData, setUserData] = useState<DocumentSnapshot>(null);
  const [userCompanyData, setUserCompanyData] = useState<any>(null);
  const [userDataFmt, setUserDataFmt] = useState<any>(null);

  const [plans, setPlans] = useState<any>([]);
  const [promoCodes, setPromoCodes] = useState<any>([]);
  const [subscription, setSubscription] = useState<any>(null);
  const [isSubscriptionCheckedOnce, setIsSubscriptionCheckedOnce] = useState(false);
  const [globalSecondsIncrementor, setGlobalSecondsIncrementor] = useState<() => void>(null);

  const [isTrial, setIsTrial] = useState(undefined);
  const [trialTimeLeft, setTrialTimeLeft] = useState<number>(null);
  const [isTrialExpired, setIsTrialExpired] = useState(false);

  const [staticData, setStaticData] = useState<DocumentData>({});
  const [signupData, setSignupData] = useState<DocumentData>({});

  const [actyTutors, setActyTutors] = useState([]);

  const [tags, setTags] = useState<DocumentData[]>([]);
  const [authors, setAuthors] = useState<DocumentData[]>([]);
  const [videos, setVideos] = useState<DocumentData[]>([]);
  const [comingSoonVideos, setComingSoonVideos] = useState<DocumentData[]>([]);
  const [mostViewsVideos, setMostViewsVideos] = useState<DocumentData[]>([]);
  const [freeVideos, setFreeVideos] = useState<DocumentData[]>([]);
  const [keepWatching, setKeepWatching] = useState<DocumentData[]>([]);
  const [newVideos, setNewVideos] = useState<DocumentData[]>([]);
  const [billboard, setBillboard] = useState<DocumentData>(null);
  const [series, setSeries] = useState<DocumentData[]>([]);
  const [seriesAuthors, setSeriesAuthors] = useState<{ author: DocumentData; authorVideos: DocumentData[] }[]>([]);
  const [seriesTags, setSeriesTags] = useState<{ tag: DocumentData; tagVideos: DocumentData[] }[]>([]);
  const [hasMoreSeriesTags, setHasMoreSeriesTags] = useState(false);
  const [loadingMoreSeriesTags, setLoadingMoreSeriesTags] = useState(false);
  const [courses, setCourses] = useState<DocumentData[]>();
  const [recommendations, setRecommendations] = useState<DocumentData[]>([]);
  const [bonusVideos, setBonusVideos] = useState<DocumentData[]>([]);

  const [tagCards, setTagCards] = useState<{ id: string; label: string }[]>([]);
  const [myList, setMyList] = useState<DocumentData[]>(null);
  const [myListLoading, setMyListLoading] = useState(true);

  const [deviceFcmToken, setDeviceFcmToken] = useState<string>(null);
  const previousFcmToken = useRef<string>(null);

  const [notifications, setNotifications] = useState([]);
  const [notificationBadge, setNotificationBadge] = useState(0);

  const [deviceId, setDeviceId] = useState<string>(null);
  const nextSeriesTagsOffsetRef = useRef<number | null>(null);
  const videoCardsByIdRef = useRef<Record<string, any>>({});
  const localKeepWatchingSourceRef = useRef<{ videos: DocumentData[]; userData: any; subscription: boolean; staticData: DocumentData } | null>(null);

  const [track, setTrack] = useState<any>(null);
  const [socket, setSocketFire] = useState<any>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useReducer(formReducer, {
    nome: '',
    cognome: '',
    indirizzo: '',
    indirizzo2: '',
    cap: '',
    citta: '',
    provincia: '',
    email: '',
    telefono: '',
  });

  const refreshTokenInterval = useRef<number>(null);

  const _ = (...segments: string[]) => segments.join('/');

  const fv = fieldValue;

  const _getOrigin = () => {
    const origin = window.location.origin;
    if (origin.includes('//localhost') && process.env.NODE_ENV == 'production') return process.env.REACT_APP_URL;
    return origin;
  };

  const checkEmail = (email: string) => {
    return auth.fetchSignInMethodsForEmail(email);
  };

  const sendEmailVerification = (path = '/') => {
    return currentUser.sendEmailVerification({ url: _getOrigin() + path });
  };

  const recoverPassword = (email: string) => {
    return auth.sendPasswordResetEmail(email, { url: _getOrigin() });
  };

  const changePassword = (password: string) => {
    return currentUser.updatePassword(password);
  };

  const reauthenticate = (password: string) => {
    const credential = emailAuthProvider(currentUser.email, password);
    return currentUser.reauthenticateWithCredential(credential);
  };

  const reauthenticateGoogle = () => {
    return currentUser.reauthenticateWithPopup(googleAuthProvider);
  };

  const signup = (email: string, password: string) => {
    return auth.createUserWithEmailAndPassword(email, password);
  };

  const login = (email: string, password: string) => {
    return auth.signInWithEmailAndPassword(email, password);
  };

  const loginWithProvider = (providerId: 'apple.com' | 'google.com', authProvider: firebase.auth.AuthProvider) => {
    return (() => {
      if (isNative) return cfaSignIn(providerId).toPromise();
      return auth.signInWithPopup(authProvider);
    })();
    // .then((result) => {
    //   const credentials = isNative ? result.userCredential : result.credential;
    //   return currentUser.linkWithCredential(credentials);
    // })
    // .then((linkResult) => {
    //   return auth.signInWithCredential(linkResult.credentials);
    // });
  };

  const loginWithGoogle = () => {
    return loginWithProvider('google.com', googleAuthProvider);
  };

  const loginWithApple = () => {
    return loginWithProvider('apple.com', appleAuthProvider);
  };

  const loginWithToken = (token: string) => {
    return auth.signInWithCustomToken(token);
  };

  const logout = async () => {
    // try {
    //   await messaging?.deleteToken();
    // } catch {}
    localKeepWatchingSourceRef.current = null;
    await clearWatchingInfo(currentUser.uid, realtimeDatabase);
    localStorage.removeItem('currentLanguage');
    document.cookie = `AUTO=;expires=${new Date(0).toUTCString()}`;
    setDeviceFcmToken(null);
    return (() => {
      if (isNative) return cfaSignOut().toPromise();
      return auth.signOut();
    })();
  };

  const logoutAll = async () => {
    localKeepWatchingSourceRef.current = null;
    await realtimeDatabase.ref(`users/${currentUser.uid}/onDisconnect`).update({ value: firebase.database.ServerValue.TIMESTAMP });
    // await messaging?.deleteToken();
    setDeviceFcmToken(null);
    await fn(CloudFunctions.LogoutAll);
  };

  const deleteAccount = async () => {
    // await messaging?.deleteToken();
    setDeviceFcmToken(null);
    return currentUser.delete();
  };

  const resetPassword = (email: string) => {
    return auth.sendPasswordResetEmail(email);
  };

  const updateEmail = (email: string) => {
    return currentUser.updateEmail(email);
  };

  const updatePassword = (password: string) => {
    return currentUser.updatePassword(password);
  };

  const mfaGetSession = () => {
    return currentUser.multiFactor.getSession();
  };

  const mfaEnroll = (assertion: firebase.auth.MultiFactorAssertion) => {
    return currentUser.multiFactor.enroll(assertion);
  };

  const mfaUnenroll = (factorId: string) => {
    return currentUser.multiFactor.unenroll(factorId);
  };

  const getTvLoginCode = async () => {
    return fn(CloudFunctions.GetTvLoginCode);
  };

  const consumeTvLoginCode = async (loginCode: string) => {
    return fn(CloudFunctions.ConsumeTvLoginCode, { loginCode });
  };

  const clearTvLoginCode = async (loginCode: string) => {
    return fn(CloudFunctions.ClearTvLoginCode, { loginCode });
  };

  const listenForTvLoginToken = (code: string) => {
    return new Promise<string>((resolve) => {
      const unsubscribe = database.doc(_('login_codes', code)).onSnapshot((snap) => {
        const { loginToken } = snap.data();
        if (!loginToken) return;
        unsubscribe();
        resolve(loginToken);
        clearTvLoginCode(code).catch(console.error);
      });
    });
  };

  const getDeviceIdentifier = async () => {
    if (deviceId) return deviceId;
    const id = await calculateDeviceIdentifier();
    setDeviceId(id);
    return id;
  };

  const checkDeviceRegistrationAbility = async (_deviceId: string) => {
    if (!subscription) return { deviceId: _deviceId, canRegister: true };
    if (userDataFmt?.admin) return { deviceId: _deviceId, canRegister: true };
    const maximumNumberOfDevices = userDataFmt?.customDevices || +subscription.items[0].price?.plan?.metadata?.DEVICES || 2;
    const devicesCollection = await userData?.ref.collection('devices').get();
    if (devicesCollection.docs.map((doc) => doc.id).includes(_deviceId)) return { deviceId: _deviceId, canRegister: true, existing: true };
    const currentAmountOfDevices = devicesCollection.docs.length;
    return { deviceId: _deviceId, canRegister: currentAmountOfDevices < maximumNumberOfDevices };
  };

  const registerDevice = async ({ deviceId, ...deviceData }, existing = false) => {
    if (existing) {
      await userData?.ref.collection('devices').doc(deviceId).update({ ts: fieldValue.serverTimestamp() });
    } else {
      await userData?.ref
        .collection('devices')
        .doc(deviceId)
        .set({ deviceId, ...deviceData, ts: fieldValue.serverTimestamp(), lockedUntil: timestamp.fromDate(startOfDay(addDays(new Date(), 30))) });
    }
    return deviceId;
  };

  const removeDevice = (deviceId: string) => {
    return userData?.ref.collection('devices').doc(deviceId).delete();
  };

  const getRegisteredDevices = async () => {
    const collection = await userData?.ref.collection('devices').get();
    if (!collection) return [];
    return collection.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  const addFcmToken = (token: string) => {
    return userData?.ref.collection('fcm').doc(token).set({ created: fieldValue.serverTimestamp() });
  };

  const removeFcmToken = (token: string) => {
    return userData?.ref.collection('fcm').doc(token).delete();
  };

  const addToMyList = async (id: string) => {
    setMyListLoading(true);
    await userData?.ref.update({ list: fieldValue.arrayUnion(id) });
    if (currentUser) await getUpdatedUserData();
  };

  const removeFromMyList = async (id: string) => {
    setMyListLoading(true);
    await userData?.ref.update({ list: fieldValue.arrayRemove(id) });
    if (currentUser) await getUpdatedUserData();
  };

  const cacheVideoCards = (items: any[] = []) => {
    items.forEach((video) => {
      if (!video?.id) return;
      videoCardsByIdRef.current[video.id] = video;
    });
    return items;
  };

  const getCachedVideoCardsByIds = (ids: string[] = []) => ids.map((id) => videoCardsByIdRef.current[id]).filter(Boolean);

  const getVideoCardsByIds = async (ids: string[] = []) => {
    if (!ids.length) return { items: [] };
    return fn(CloudFunctions.GetVideoCardsByIds, { ids, language: currentLanguage, countryProv: country_code }).then((data) => {
      data.items = cacheVideoCards(data.items || []);
      return data;
    });
  };

  const updateMyList = async (inputList?: any[], options: { userData?: any; videos?: any[] } = {}) => {
    if (!inputList?.length) {
      setMyList([]);
      setMyListLoading(false);
      return;
    }

    const effectiveUserData = options.userData ?? userDataFmt;
    const effectiveVideos = options.videos ?? videos;

    if (!shouldUseLoadingPerf(effectiveUserData, staticData)) {
      const legacyItems = getLegacyMyListItems(inputList, effectiveVideos);
      if (legacyItems === null) return;
      setMyList(legacyItems);
      setMyListLoading(false);
      return;
    }

    try {
      const { items = [] } = await getVideoCardsByIds(inputList);
      setMyList(items);
    } catch (error) {
      console.error(error);
    } finally {
      setMyListLoading(false);
    }
  };

  const getSeries = (seriesId: string) => {
    return series.find((serie) => serie.id == seriesId);
  };

  const getCourse = async (courseId: string) => {
    return fn(CloudFunctions.GetCourse, { courseId });
  };

  const getTest = async (courseId: string, id: string) => {
    const course = await getCourse(courseId);
    if (!course) return null;
    return course.tests.find((test) => test.id == id);
  };

  const validateTest = async (data: any) => {
    return fn(CloudFunctions.ValidateTest, data);
  };

  const addCheckoutSession = async (price: string, promotion_code: string = null, to_ecm: boolean = false, from_shop = false, is_package = false) => {
    const params: string = is_package ? '&pk=true' : from_shop ? '&fs=true' : '';
    const subscription: any = {
      price,
      locale: currentLanguage,
      success_url: _getOrigin() + '/success?sid={CHECKOUT_SESSION_ID}' + params,
      cancel_url: _getOrigin() + (window.location.pathname.endsWith('wait') ? '/' : window.location.pathname) + window.location.search,
      allow_promotion_codes: true,
      collect_shipping_address: true,
      tax_id_collection: isItalian(country_code),
      custom_text: {
        shipping_address: {
          message:
            currentLanguage == 'it'
              ? 'Per ricevere la fattura elettronica è necessario inserire la ragione sociale e la partita Iva all\'interno della voce "Sto acquistando come attività".\n In caso non desiderassi la fattura elettronica puoi procedere all\'acquisto senza la compilazione del suddetto campo.'
              : '',
        },
      },
      metadata: {},
    };

    if (to_ecm) {
      subscription.metadata.TO_ECM = to_ecm;
      subscription.metadata.FIREBASE_UID = currentUser.uid;
    }

    if (promotion_code) subscription.promotion_code = promotion_code;

    const ref = await database.collection(_('userData', currentUser.uid, 'checkout_sessions')).add(subscription);
    return new Promise<{ sessionId: string; url: string }>((resolve, reject) => {
      const unsubscribe = ref.onSnapshot((snap) => {
        const { error, sessionId, url } = snap.data();
        if (!error && (!sessionId || !url)) return;
        unsubscribe();
        if (error) reject(error);
        resolve({ sessionId, url });
      });
    });
  };

  const getCustomerPortalUrl = async (isCustomFlow?: boolean) => {
    const parsedUrl = isCustomFlow ? '/buy_ecm?resumeFlow=true' : '/profile';
    return fn(CloudFunctions.CreatePortalLink, { returnUrl: _getOrigin() + parsedUrl });
  };

  const updateAvailableCredits = async (totalSelectedCredits: number, subId: string) => {
    try {
      const currentSubscription = await database.doc(_('userData', currentUser.uid, 'subscriptions', subId));
      const subSnap = await currentSubscription.get();
      if (!subSnap.exists) throw new Error('Subscription not found');

      // Get current credit balance from userData (already managed by backend for renewals)
      const currentCreditBalance =
        userDataFmt?.packageECMCurrentCreditBalance !== undefined && userDataFmt?.packageECMCurrentCreditBalance !== null
          ? parseInt(userDataFmt.packageECMCurrentCreditBalance)
          : parseInt(subSnap.data().items?.[0].price?.product?.metadata.AVAILABLE_CREDITS);

      if (currentCreditBalance === undefined || currentCreditBalance === null) {
        throw new Error('Subscription does not manage credits');
      }

      const newCreditBalance = currentCreditBalance - totalSelectedCredits;
      await userData.ref.set(
        {
          packageECMCurrentCreditBalance: newCreditBalance,
          updatedAt: fieldValue.serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.error(e);
    }
  };

  const updateUserSelectedCourses = async (courses: any[], options: { skipListUpdate?: boolean } = {}) => {
    try {
      const coursesCollection = database.collection(_('userData', currentUser.uid, 'selectedCoursesFromPackages'));
      const existingCoursesSnap = await coursesCollection.get();
      const existingCourses = existingCoursesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      const batch = database.batch();
      for (const course of courses) {
        if (!existingCourses.find((c) => c.id == course.id)) {
          const courseRef = coursesCollection.doc(course.id);
          batch.set(courseRef, {
            id: course.id,
            name: course.name,
            slug: course.slug,
            unique_code: course.unique_code,
            price: course.price,
            credits: course.credits,
            selectedAt: fieldValue.serverTimestamp(),
          });
        }
      }
      await batch.commit();
      await getUpdatedUserData(options);
    } catch (e) {
      console.error(e);
    }
  };

  const updateUserData = async (data?: DocumentData, options: { skipListUpdate?: boolean } = {}) => {
    try {
      if (data) {
        await userData.ref.set({ ...data, updatedAt: fieldValue.serverTimestamp() }, { merge: true });
      }
    } catch (e) {
      console.error(e);
    }
    await getUpdatedUserData(options);
  };

  const getVideoShardedCounterIncrementor = (videoId: string): (() => Promise<void>) => {
    const videoAnalyticsDoc = database.collection('userData').doc(currentUser.uid + '/videos/' + videoId);
    const videoSecondsViews = new sharded.Counter(videoAnalyticsDoc, 'views');
    return () => videoSecondsViews.incrementBy(1);
  };

  const getVideoOpeningsShardedCounterIncrementor = (videoId: string): (() => Promise<void>) => {
    const videoAnalyticsDoc = database.collection('userData').doc(currentUser.uid + '/videos/' + videoId);
    const videoOpenings = new sharded.Counter(videoAnalyticsDoc, 'openings');
    return () => videoOpenings.incrementBy(1);
  };

  const updateUserVideoAnalytics = async (videoId: string, data: DocumentData) => {
    try {
      const videoAnalyticsDoc = database.doc(_('userData', currentUser.uid, 'videos', videoId));
      await videoAnalyticsDoc.set(data, { merge: true });
    } catch {}
    // await getUpdatedUserData();
  };

  const addNotifications = (items: any[]) => {
    setNotifications((previousNotifications) => [
      ...items.filter((i) => {
        const id = i.data?.id;
        if (!id) return true;
        return !previousNotifications.find((f) => {
          const prevId = f.data?.id;
          if (!prevId) return false;
          return prevId == id;
        });
      }),
      ...previousNotifications,
    ]);
  };

  const readNotification = (id: number) => {
    setNotifications((previousNotifications) =>
      previousNotifications.map((notification) => {
        if (notification.data.id != id) return notification;
        return { ...notification, read: true };
      })
    );
  };

  const cleanNotifications = () => {
    setNotifications((previousNotifications) => previousNotifications.filter((n) => !n.read));
  };

  // const prevNotifications = new Set<string>();
  // useEffect(() => {
  //   const unsubscribe = messaging?.onMessage((event) => {
  //     const { notification, data, ...rest } = event;
  //     console.log(event);
  //     if (prevNotifications.has(data.id)) return;
  //     prevNotifications.add(data.id);
  //     addNotifications([{ ...notification, data, read: false }]);
  //     toast(
  //       <div
  //         className={'d-flex align-items-center'}
  //         onClick={() => {
  //           if (!notification.read) {
  //             const id = notification.id ?? notification.data?.id;
  //             readNotification(id);
  //           }
  //           const action = notification.data?.action;
  //           if (!action) return;
  //           if (action.startsWith('goto::')) {
  //             const [, path] = action.split('::');
  //             history.push(path);
  //           }
  //         }}
  //       >
  //         <FontAwesomeIcon icon={faBell} className={'mr-3'} />
  //         <div>
  //           <div>{notification.title}</div>
  //           <div className={'text-muted'}>{notification.body}</div>
  //         </div>
  //       </div>
  //     );
  //   });
  //   return () => unsubscribe?.();
  // }, [track, socket]);

  const [seoUpdateInProgress, setSeoUpdateInProgress] = useState(false);
  const [seoUpdateLogs, setSeoUpdateLogs] = useState<any[]>([]);
  useEffect(() => {
    database.doc('system/systemData').onSnapshot((doc) => {
      const { seoUpdateInProgress = false, seoUpdateLogs = [] } = doc.exists ? doc.data() : {};
      setSeoUpdateInProgress(seoUpdateInProgress);
      setSeoUpdateLogs(seoUpdateLogs);
    });
  }, []);

  useEffect(() => {
    getStaticData().catch(console.error);

    database
      .collection('plans')
      .where('active', '==', true)
      .where('metadata.PLAN', '==', 'yes')
      .get()
      .then(async (querySnapshot) => {
        const plans = [];
        for (const planDoc of querySnapshot.docs) {
          const plan: { [p: string]: any } = { id: planDoc.id, ...planDoc.data(), prices: [] };
          const priceSnap = await planDoc.ref.collection('prices').get();
          for (const priceDoc of priceSnap.docs) {
            const price: { [p: string]: any } = { id: priceDoc.id, ...priceDoc.data() };
            if (price.active) plan.prices.push(price);
          }
          plan.description = plan.metadata?.[`i18n.${currentLanguage}.description`] || plan.description;
          plan.name = plan.metadata?.[`i18n.${currentLanguage}.name`] || plan.name;
          plans.push(plan);
        }
        setPlans(plans);
      })
      .catch(console.error);

    fn(CloudFunctions.GetStripeCoupons).then((data) => setPromoCodes(data));
  }, []);

  useEffect(() => {
    const unread = notifications.filter((n) => !n.read).length;
    setNotificationBadge(unread);
  }, [notifications]);

  useEffect(() => {
    if (deviceFcmToken) addFcmToken(deviceFcmToken)?.catch(console.error);
    else if (previousFcmToken.current) removeFcmToken(previousFcmToken.current)?.catch(console.error);
  }, [deviceFcmToken, userData]);

  useEffect(() => {
    previousFcmToken.current = deviceFcmToken;
  }, [deviceFcmToken, userData]);

  useEffect(() => {
    return auth.onAuthStateChanged((user) => {
      console.info({ user: JSON.parse(JSON.stringify(user)) });
      setCurrentUser(user);
      setIsLoggedIn(!!user);
      setLoading(false);
      if (user) {
        user.getIdTokenResult().then((tokenResult) => {
          setUserClaims(tokenResult.claims);
        });
      } else {
        setUserClaims({});
      }
    });
  }, []);

  useEffect(() => {
    if (currentUser) {
      getUserCompanyData().catch(console.error);
    }
  }, [currentUser, userDataFmt, userData]);

  const getUserCompanyData = async () => {
    if (currentUser) {
      const userData = await database.doc(_('userData', currentUser.uid)).get();
      if (userData.data().company) {
        const companyDoc = await userData.data().company.get();
        if (companyDoc.exists) {
          const companyData = companyDoc.data();
          const data = { id: companyDoc.id, ...companyData };
          if (companyData.logo) {
            const logo = videoDataStorage.child(`companies_data/${companyDoc.id}/${companyData.logo}`);
            try {
              data.logoUrl = await logo.getDownloadURL();
            } catch (_) {}
          }
          setUserCompanyData(data);
          return;
        }
      }
    }
    setUserCompanyData(null);
  };

  useEffect(() => {
    localKeepWatchingSourceRef.current = null;
  }, [currentUser?.uid]);

  useEffect(() => {
    let disconnect: firebase.database.OnDisconnect;

    if (!currentUser) {
      disconnect?.cancel();
      realtimeDatabase?.goOffline();

      localKeepWatchingSourceRef.current = null;
      setUserData(null);
      setUserDataFmt(null);
      setSubscription(null);
      setIsSubscriptionCheckedOnce(false);
      setGlobalSecondsIncrementor(null);
      setIsTrial(null);
      setIsTrialExpired(false);
      setTrialTimeLeft(null);
      setDeviceFcmToken(null);
      setDeviceId(null);
      Sentry.configureScope((scope) => scope.setUser(null));
      (window as any).BrevoConversations?.('updateIntegrationData', { email: null });
    } else {
      if (!currentUser.toJSON) return;

      (window as any).BrevoConversations?.('updateIntegrationData', { email: currentUser.email });
      (window as any).Brevo.push(function () {
        (window as any).Brevo.identify({
          identifiers: { email_id: currentUser.email },
        });
      });
      Sentry.setUser({ id: currentUser.uid, email: currentUser.email });
      analytics.setUserId(currentUser.uid);
      try {
        (window as any).aa('setUserToken', currentUser.uid);
      } catch (e) {
        console.error(e);
      }

      realtimeDatabase.goOnline();
      (async () => {
        const clientName = `client_${await calculateDeviceIdentifier()}`;
        const rtdbUserRecord = realtimeDatabase.ref(`users/${currentUser.uid}/${clientName}`);
        const rtdbOnDisconnectRecord = realtimeDatabase.ref(`users/${currentUser.uid}/onDisconnect`);
        const PresenceDatabase = realtimeDatabase.ref('.info/connected');
        let onDisconnectTimestampState = false;

        rtdbUserRecord.on('value', (_) => {
          // callback esistente solo per mantenere attiva la connessione al RTDB,
          // altrimenti Android creerebbe problemi
        });
        rtdbOnDisconnectRecord.on('value', async (snapshot) => {
          if (!snapshot.val() || !onDisconnectTimestampState) {
            onDisconnectTimestampState = true;
            return;
          }
          setIsLoggedIn(false);
          await logout();
          window.location.href = '/';
        });
        PresenceDatabase.on('value', async (snapshot) => {
          if (!snapshot.val()) return;
          disconnect = rtdbUserRecord.onDisconnect();
          disconnect
            .update({
              online: firebase.database.ServerValue.increment(-1),
            })
            .catch(console.error);

          rtdbUserRecord
            .update({
              online: firebase.database.ServerValue.increment(1),
            })
            .catch(console.error);
        });
      })();
      // https://stackoverflow.com/a/56933697
      document.addEventListener('visibilitychange', () => {
        const isVisible = document.visibilityState == 'visible';
        if (isVisible) {
          if (refreshTokenInterval.current) {
            clearInterval(refreshTokenInterval.current);
            refreshTokenInterval.current = null;
          }
        } else {
          currentUser.getIdToken(true).catch(console.error);
          refreshTokenInterval.current = setInterval(() => {
            currentUser.getIdToken(true).catch(console.error);
          }, MINUTES(10)) as unknown as number;
        }
      });

      // if (messaging) messaging.getToken().then(setDeviceFcmToken).catch(console.error);

      getUpdatedUserData().then((userData) => {
        getStaticData().then((staticData) => {
          const trialFullTime = userData.customTrialSeconds || staticData.DEFAULT_TRIAL_SECONDS;
          const trialExtension = userData.trialExtension || 0;

          const totalViews = new sharded.Counter(database.doc(_('userData', currentUser.uid)), 'views');
          setGlobalSecondsIncrementor(() => () => totalViews.incrementBy(1));

          if (userData.preferredLanguage && userData.preferredLanguage != currentLanguage) {
            if (supportedLanguages.includes(userData.preferredLanguage)) {
              return updateLanguage(userData.preferredLanguage);
            }
          }

          database
            .collection(_('userData', currentUser.uid, 'subscriptions'))
            .where('status', 'in', ['active', 'past_due'])
            .get()
            .then(async (snapshot) => {
              const doc = snapshot.docs[0];

              (window as any).GeniusChat?.identify(
                {
                  name: `${userData.profile?.formData?.nome || ''} ${userData.profile?.formData?.cognome || ''}`,
                  email: currentUser.email,
                  external_id: currentUser.uid,
                },
                {
                  has_subscription: !!doc,
                  category: userData.profile?.formData?.categoriaProfessionale,
                }
              );
              (window as any).GeniusChat?.setLang(userData.preferredLanguage || 'it');
              (window as any).GeniusChat?.configure({ className: 'genius-chat-launcher' });

              if (canAccessFeature('GENIUS', currentLanguage, staticData, userData)) {
                await (window as any).GeniusChat?.load();
                const iframe = document.querySelector("iframe[src*='genius']") as HTMLIFrameElement;
                if (iframe) iframe.style.borderColor = 'black';
              }

              if (doc) {
                setIsTrial(false);
                const fmt: any = { id: doc.id, ...doc.data() };
                const productData = await doc.data().product.get();
                if (productData.exists) {
                  fmt.items[0].price.product = productData.data();
                }
                setSubscription(fmt);
              } else {
                setIsTrial(true);
                totalViews.onSnapshot((snap) => {
                  const secondsElapsed = snap.data() || 0;
                  const trialSecondsLeft = trialFullTime + trialExtension - secondsElapsed;
                  setTrialTimeLeft(trialSecondsLeft);
                  setIsTrialExpired(trialSecondsLeft < 0);
                });
              }
              setIsSubscriptionCheckedOnce(true);
            });

          fn(CloudFunctions.GetPreVideoData, { language: currentLanguage }).then((data) => {
            const { tags, authors } = data;
            setTags(tags);
            setAuthors(authors);

            if (userData.experiments?.[Experiments.InfiniteScroll]) {
              Promise.all([
                fn(CloudFunctions.GetBillboard, { language: currentLanguage }).then((data) => {
                  const { billboard } = data;
                  setBillboard(billboard);
                }),
                fn(CloudFunctions.GetComingSoon).then((data) => {
                  const { comingSoon = [] } = data;
                  setComingSoonVideos(comingSoon);
                }),
                fn(CloudFunctions.GetMostViews, { language: currentLanguage }).then((data) => {
                  const { mostViews = [] } = data;
                  setMostViewsVideos(mostViews);
                }),
                fn(CloudFunctions.GetKeepWatching).then((data) => {
                  const { keepWatching = [] } = data;
                  localKeepWatchingSourceRef.current = null;
                  setKeepWatching(keepWatching);
                }),
                fn(CloudFunctions.GetNewVideos).then((data) => {
                  const { newVideos = [] } = data;
                  setNewVideos(newVideos);
                }),
              ]).then(() => {
                setSeriesTags(
                  tags
                    .filter((tag) => tag.showAsSeries && (!tag.visibleFor || tag.visibleFor[userData.profile?.formData?.categoriaProfessionale]))
                    .map((tag) => ({ tag, tagVideos: [] }))
                    .sort((a, b) => b.tag.order - a.tag.order)
                );
                getUpdatedUserData().catch(console.error);
              });
            } else {
              const useLoadingPerf = shouldUseLoadingPerf(userData, staticData);

              fn(CloudFunctions.GetBillboard, { language: currentLanguage }).then((data) => {
                const { billboard } = data;
                setBillboard(billboard);
              });

              if (useLoadingPerf) {
                fn(CloudFunctions.GetVideosBrowseSeries, { language: currentLanguage })
                  .then(({ series = [] }) => setSeries(series))
                  .catch(console.error);
              }

              Promise.all([
                useLoadingPerf
                  ? fn(CloudFunctions.GetVideosBrowse, { language: currentLanguage, countryProv: country_code })
                  : fn(CloudFunctions.GetVideosNew, { language: currentLanguage, countryProv: country_code }),
                (() => {
                  if (
                    staticData.RECOMMENDATIONS_LANGUAGES.includes(currentLanguage) &&
                    (staticData.ENABLE_RECOMMENDATIONS || userData.experiments?.[Experiments.Recommendations])
                  ) {
                    try {
                      return manualCloudFunction(CloudFunctions.Recommendations, currentUser, { id_user: currentUser.uid });
                    } catch {
                      return Promise.resolve({});
                    }
                  }
                  return Promise.resolve({});
                })(),
              ]).then(async ([data, result]) => {
                let videosForRecommendations: any[] = [];
                let seriesForRecommendations: any[] = [];
                let seriesTagsForRecommendations: any[] = [];
                let mostViewsForRecommendations: any[] = [];
                let listSourceVideos: any[] | undefined;

                if (useLoadingPerf) {
                  localKeepWatchingSourceRef.current = null;
                  const { mergedItems, comingSoon, mostViews, freeVideos, keepWatching, newVideos, bonusVideos, mappedSeriesTags } = mapBrowsePayload(data);
                  videosForRecommendations = mergedItems;
                  seriesTagsForRecommendations = mappedSeriesTags;
                  mostViewsForRecommendations = mostViews;
                  listSourceVideos = mergedItems;

                  setVideos(mergedItems);
                  setSeriesTags(mappedSeriesTags);
                  setComingSoonVideos(comingSoon);
                  setMostViewsVideos(mostViews);
                  setFreeVideos(freeVideos);
                  localKeepWatchingSourceRef.current = null;
                  setKeepWatching(keepWatching);
                  setNewVideos(newVideos);
                  setBonusVideos(bonusVideos);
                  fetchSeriesTagsPage(0, mergedItems).catch(console.error);
                } else {
                  const { videos, comingSoon = [], series = [], seriesTags = [], mostViews = [], freeVideos = [] } = data;
                  videosForRecommendations = videos;
                  seriesForRecommendations = series;
                  seriesTagsForRecommendations = seriesTags;
                  mostViewsForRecommendations = mostViews;
                  listSourceVideos = videos;

                  setVideos(videos);
                  setSeries(series);
                  setSeriesTags(seriesTags);
                  setComingSoonVideos(comingSoon);
                  setMostViewsVideos(mostViews);
                  setFreeVideos(freeVideos);
                  setBonusVideos(videos.filter((x) => x.bonus));

                  const subscriptionCollection = await database
                    .collection(_('userData', currentUser.uid, 'subscriptions'))
                    .where('status', 'in', ['active'])
                    .get();
                  const subscription = !!subscriptionCollection.docs[0];

                  localKeepWatchingSourceRef.current = {
                    videos,
                    userData,
                    subscription,
                    staticData,
                  };

                  const keepWatching = videos
                    .filter((video) => video.hasPercentage && !video.hidden)
                    .filter((video) => !isVideoLocked(video, userData, subscription, staticData))
                    .slice(0, 10);

                  setKeepWatching(keepWatching);
                  setNewVideos(
                    videos
                      .filter((video) => video.isNew && (!video.languages || video.languages.includes(currentLanguage)))
                      .sort((a, b) => (b.created?._seconds ?? 0) - (a.created?._seconds ?? 0))
                      .slice(0, 6)
                  );
                }

                setHasMoreSeriesTags(false);
                setLoadingMoreSeriesTags(false);
                nextSeriesTagsOffsetRef.current = null;

                getUpdatedUserData({ listSourceUserData: userData, listSourceVideos }).catch(console.error);

                // result: recommendations
                if (!result) return;
                if (!result.recommendations_series || !result.recommendations_videos || !result.recommendations_confidence) return;
                if (!result.recommendations_series.length || !result.recommendations_videos.length || !result.recommendations_confidence.length) return;
                const zipped = result.recommendations_videos.map((v, i) => ({
                  videoId: v,
                  seriesId: result.recommendations_series[i],
                  confidence: result.recommendations_confidence[i],
                }));

                const recs = useLoadingPerf
                  ? await (async () => {
                      const recommendationIds = zipped.map((entry) => entry.videoId);
                      const recommendationMap = new Map<string, { videoId: string; seriesId: string; confidence: number; index: number }>(
                        zipped.map((entry, index) => [entry.videoId, { ...entry, index }])
                      );
                      const { items: recommendationItems = [] } = await getVideoCardsByIds(recommendationIds);
                      const recommendations = mapRecommendationsByRequestedIds(recommendationIds, recommendationItems, recommendationMap);
                      cacheVideoCards(recommendations);
                      return recommendations;
                    })()
                  : zipped
                      .map(addConfidence(videosForRecommendations, seriesTagsForRecommendations, mostViewsForRecommendations, seriesForRecommendations, true))
                      .filter(Boolean);

                setRecommendations(recs);
              });
            }
          });
        });

        database
          .doc(_('system', 'signupData'))
          .get()
          .then((doc) => {
            setSignupData({ id: doc.id, ...doc.data() });
          });
      });
    }
    return () => {
      realtimeDatabase.goOffline();
    };
  }, [currentUser]);

  const setValidBonus = (bonusDocumentId: string) => {
    setBonusVideos((prev) => {
      return prev.map((video) => {
        if (video.bonus == bonusDocumentId) {
          video.isValidBonus = true;
        }
        return video;
      });
    });
  };

  useEffect(() => {
    extractTagCards(videos);
  }, [tags]);

  const getStaticData = async () => {
    if (staticData && Object.keys(staticData).length) return staticData;
    const _staticData = await fn(CloudFunctions.GetStaticData, { language: currentLanguage });
    setStaticData(_staticData);
    return _staticData;
  };

  const mapBrowsePayload = (data: any, existingItems: any[] = []) => {
    const {
      items = [],
      comingSoonIds = [],
      mostViewIds = [],
      freeVideoIds = [],
      keepWatchingIds = [],
      newVideoIds = [],
      bonusVideoIds = [],
      seriesTags = [],
    } = data || {};

    const itemsMap = new Map(existingItems.map((video) => [video.id, video]));
    items.forEach((video) => itemsMap.set(video.id, video));
    const mergedItems = Array.from(itemsMap.values());
    cacheVideoCards(mergedItems);
    const getItems = (ids: string[]) => ids.map((id) => itemsMap.get(id)).filter(Boolean);

    return {
      mergedItems,
      comingSoon: getItems(comingSoonIds),
      mostViews: getItems(mostViewIds),
      freeVideos: getItems(freeVideoIds),
      keepWatching: getItems(keepWatchingIds),
      newVideos: getItems(newVideoIds),
      bonusVideos: getItems(bonusVideoIds),
      mappedSeriesTags: (seriesTags || []).map(({ tag, videoIds }) => ({
        tag,
        tagVideos: getItems(videoIds || []),
      })),
    };
  };

  const fetchSeriesTagsPage = async (offset: number, existingItems: any[] = videos) => {
    const data = await fn(CloudFunctions.GetVideosBrowseSeriesTags, {
      language: currentLanguage,
      countryProv: country_code,
      seriesTagsOffset: offset,
      seriesTagsLimit: 8,
    });

    const { mergedItems, mappedSeriesTags } = mapBrowsePayload(data, existingItems);
    setVideos(mergedItems);
    setSeriesTags((previous) => {
      const byTagId = new Map(previous.map((serie) => [serie.tag.id, serie]));
      mappedSeriesTags.forEach((serie) => byTagId.set(serie.tag.id, serie));
      return Array.from(byTagId.values());
    });
    setHasMoreSeriesTags(!!data.hasMoreSeriesTags);
    nextSeriesTagsOffsetRef.current = data.nextSeriesTagsOffset ?? null;
  };

  const loadMoreSeriesTags = async () => {
    if (loadingMoreSeriesTags) return;
    if (!hasMoreSeriesTags) return;
    if (nextSeriesTagsOffsetRef.current == null) return;

    setLoadingMoreSeriesTags(true);
    try {
      await fetchSeriesTagsPage(nextSeriesTagsOffsetRef.current);
    } finally {
      setLoadingMoreSeriesTags(false);
    }
  };

  const queryVideosByString = async (query: string) => {
    const start = Date.now();
    const data = await fn(CloudFunctions.QueryVideos, { query, useReducedParse: true, language: currentLanguage, countryProv: country_code });
    return { data, time: Date.now() - start };
  };

  const extractTagCards = (videos: any[]) => {
    const countedTags: { [key: string]: number } = videos.reduce((counter, current) => {
      const videoTags = current.tags ?? [];
      for (const { id } of videoTags) {
        if (!counter[id]) counter[id] = 0;
        counter[id]++;
      }
      return counter;
    }, {});

    const tagCards = stableSort(Object.entries(countedTags), ([, a], [, b]) => b - a)
      .map(([tagId]) => ({ id: tagId, label: tags[tagId] }))
      .filter((tag) => tag.label)
      .slice(0, 5);

    setTagCards(tagCards);
  };

  const getUpdatedUserData = async (options: { skipListUpdate?: boolean; listSourceVideos?: any[]; listSourceUserData?: any } = {}) => {
    const doc = await database.doc(_('userData', currentUser.uid)).get();
    setUserData(doc);
    const videoDataCollection = await database.collection(_('userData', currentUser.uid, 'videos')).get();
    const videoData = videoDataCollection.docs.map((videoDoc) => ({ id: videoDoc.id, ...videoDoc.data() }));
    const ecmShopDataCollection = await database.collection(_('userData', currentUser.uid, 'ecm_shop')).get();
    const ecmShopData = ecmShopDataCollection.docs.map((ecmDoc) => ({ id: ecmDoc.id, ...ecmDoc.data() }));
    const ecmPackageCollection = await database.collection(_('userData', currentUser.uid, 'selectedCoursesFromPackages')).get();
    const ecmPackageData = ecmPackageCollection.docs.map((ecmDoc) => ({ id: ecmDoc.id, ...ecmDoc.data() }));
    const fadCoursesCollection = await database.collection(_('userData', currentUser.uid, 'fad_courses')).get();
    const fadCourses = fadCoursesCollection.docs.map((fadDoc) => ({ id: fadDoc.id, ...fadDoc.data() }));
    const notifications = await doc.ref.collection('notifications').orderBy('created', 'desc').get();
    const notificationsData = notifications.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const selectedCoursesFromPackagesCollection = await database.collection(_('userData', currentUser.uid, 'selectedCoursesFromPackages')).get();
    const selectedCoursesFromPackages = selectedCoursesFromPackagesCollection.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const activeSubscription = await database.collection(_('userData', currentUser.uid, 'subscriptions')).where('status', 'in', ['active', 'past_due']).get();
    const activeSubscriptionData = activeSubscription.docs.length ? true : null;
    setNotifications(notificationsData);
    const fmt: any = {
      id: doc.id,
      ...doc.data(),
      videoData,
      ecmShopData,
      notifications: notificationsData,
      selectedCoursesFromPackages,
      ecmPackageData,
      activeSubscription: activeSubscriptionData,
      fadCourses,
    };
    setUserDataFmt(fmt);
    if (!options.skipListUpdate) {
      await updateMyList(fmt?.list || [], {
        userData: options.listSourceUserData ?? fmt,
        videos: options.listSourceVideos,
      });
    }
    return fmt;
  };

  const isVideoLocked = (video, userData?, sub?, staticDataOverride?) => {
    const hasCompanyAccess = hasVerifiedCompanyAccess(video, userCompanyData?.id);

    return shouldLockVideoForUser(
      {
        currentUser,
        subscription: sub != null ? sub : subscription,
        video,
        hasCompanyAccess,
      },
      staticDataOverride ?? staticData
    );
  };

  const recomputeLocalKeepWatching = () => {
    const source = localKeepWatchingSourceRef.current;
    if (!source) return;

    setKeepWatching(
      source.videos
        .filter((video) => video.hasPercentage && !video.hidden)
        .filter((video) => !isVideoLocked(video, source.userData, source.subscription, source.staticData))
        .slice(0, 10)
    );
  };

  useEffect(() => {
    if (!localKeepWatchingSourceRef.current) return;
    recomputeLocalKeepWatching();
  }, [userCompanyData]);

  const requestImpersonationToken = async (uid: string, sender: string) => {
    return fn(CloudFunctions.RequestImpersonationToken, { uid, sender });
  };

  const getVideosForTag = async (tagId: string) => {
    return fn(CloudFunctions.GetVideosForTag, { tagId, language: currentLanguage });
  };

  const requestVideoData = async (videoId: string, drmType: string) => {
    return fn(CloudFunctions.GetVideo, { videoId, drmType, language: currentLanguage, countryProv: country_code });
  };

  const updateHubspotProperties = async (properties: { [p: string]: any }) => {
    return fn(CloudFunctions.UpdateHubspotProperties, properties);
  };

  const getBrevoContactLink = async ({ uid, email }: { uid: string; email?: string | null }) => {
    return fn(CloudFunctions.GetBrevoContactLink, { uid, email });
  };

  const getTickets = async (asAdmin = false) => {
    return fn(CloudFunctions.GetTickets, { asAdmin });
  };

  const getTicket = async (ticketId, asAdmin = false) => {
    return fn(CloudFunctions.GetTicket, { ticketId, asAdmin });
  };

  const submitTicket = async (data) => {
    return fn(CloudFunctions.SubmitTicket, data);
  };

  const getTicketStatuses = async () => {
    return fn(CloudFunctions.GetTicketStatuses);
  };

  const rateTicket = async (ticketId, rating = null) => {
    return fn(CloudFunctions.RateTicket, { ticketId, rating });
  };

  const assignTicket = async (ticketId, assignee = null) => {
    return fn(CloudFunctions.AssignTicket, { ticketId, assignee });
  };

  const getUserData = async ({ uid, email, alias }: { email?: string; uid?: string; alias?: any }) => {
    return fn(CloudFunctions.GetUserData, { uid, email, alias });
  };

  const getUserCourse = async (uid: string, cid: string, action: string, payload?: any) => {
    return fn(CloudFunctions.GetUserCourse, { uid, courseId: cid, action, payload });
  };

  const getStreamingData = async (channelId: string = null) => {
    return fn(CloudFunctions.GetStreamingData, { channelId }, { useExponentialBackoff: false });
  };

  const updateUser = async (uid: string, updater: { [p: string]: any }) => {
    return fn(CloudFunctions.UpdateUser, { uid, updater });
  };

  const getPublishData = async () => {
    return fn(CloudFunctions.GetPublishData);
  };

  const requestVideoContent = async (id: string) => {
    return fn(CloudFunctions.RequestVideoContent, { id });
  };

  const getStats = async () => {
    return fn(CloudFunctions.GetStats, { language: currentLanguage });
  };

  const getLogs = async (offset = 0, elementId?: string) => {
    return fn(CloudFunctions.GetLogs, { offset, elementId });
  };

  const upsertHubspotProduct = async (id: string, objectType: 'video' | 'tag') => {
    return fn(CloudFunctions.UpsertHubspotProduct, { id, objectType });
  };

  const getLegal = async (doc: 'privacy' | 'cookie' | 'tos' | 'bal' | 'contacts', year?: number) => {
    const legal = await fn(CloudFunctions.GetLegal, { doc, y: year, language: currentLanguage });
    return legal;
  };

  const getActyFaq = async () => {
    const faq = await fn(CloudFunctions.GetActyFaq, { language: currentLanguage });
    return faq;
  };

  const getCompanies = async () => {
    return fn(CloudFunctions.GetCompanies);
  };

  const getCompanyUsers = async (companyId: string, page: number, filter?: string) => {
    return fn(CloudFunctions.GetCompanyUsers, { companyId, page, filter });
  };

  const updateCompany = async (
    {
      enabled,
      companyData,
      users,
      userToDelete,
      deleteCompany,
    }: {
      enabled?: boolean;
      companyData?: GenericObject;
      users?: { email: string; nome: string; cognome: string; password: string }[];
      userToDelete?: string;
      deleteCompany?: boolean;
    },
    companyId?: string
  ): Promise<{
    duplicated: string[];
    nonExistent: string[];
    occupied: { email: string; companyName: string }[];
    created: { email: string; uid: string; password: string }[];
    deleteErrors: any[];
  }> => {
    return fn(CloudFunctions.UpdateCompany, { companyId, enabled, companyData, usersToAdd: users, userToRemove: userToDelete });
  };

  const getVideoReports = async () => {
    return fn(CloudFunctions.GetVideoReports, { language: currentLanguage });
  };

  const getActyTutorCategories = async (): Promise<DocumentData[]> => {
    return fn(CloudFunctions.GetActyTutorCategories, { language: currentLanguage });
  };

  const getActyTutors = async (actyStartDate: Date, actyCategoryId: string): Promise<any[]> => {
    return fn(CloudFunctions.GetActyTutors, { actyStartDate: actyStartDate, actyCategoryId: actyCategoryId, language: currentLanguage });
  };

  const getActyAppointments = async (asAdmin = false, page = 1, sorting = { direction: 'desc', field: 'timestamp' }, filters: any, exportCsv: boolean) => {
    return fn(CloudFunctions.GetActyAppointments, { language: currentLanguage, asAdmin, page, sorting, filters, exportCsv });
  };

  const getActyAppointment = async (appointmentId: string) => {
    return fn(CloudFunctions.GetActyAppointment, { appointmentId: appointmentId, language: currentLanguage });
  };

  const getActyReservationUrl = (actyTutorId: string, actyCategoryId: string) => {
    return fn(CloudFunctions.GetActyReservationUrl, { actyTutorId, actyCategoryId });
  };

  const getCustomFlowUsers = () => {
    return fn(CloudFunctions.GetCustomFlowUsers);
  };

  const massiveCustomSignup = (users: any[]) => {
    return fn(CloudFunctions.MassiveCustomSignup, { users });
  };

  const getCourseStatus = (uid: string, courseIds: string[]) => {
    return fn(CloudFunctions.GetCourseStatus, { uid, courseIds });
  };

  const handleCustomUser = (uid: string, { stripeProductId, courseSlugs }: { stripeProductId?: string; courseSlugs?: string[] }) => {
    return fn(CloudFunctions.HandleCustomUser, { uid, stripeProductId, courseSlugs });
  };

  const getFeedbacks = async () => {
    return fn(CloudFunctions.GetFeedbacks);
  };

  const getStripeOneOffs = async () => {
    return fn(CloudFunctions.GetStripeOneOffs, { countryProv: country_code });
  };

  const getCheckoutDetails = async (id: string) => {
    return fn(CloudFunctions.GetCheckoutDetails, { id });
  };

  const createOneOffSession = async (data: any) => {
    return fn(CloudFunctions.CreateOneOffSession, { data, language: currentLanguage });
  };

  const updateCustomerSubscription = async (
    priceId?: string,
    upgradeInterval?: boolean,
    finalize?: string,
    cancel?: string,
    toECM?: boolean,
    isPreviewMode?: boolean,
    productToUpgradeToId?: string
  ) => {
    return fn(CloudFunctions.UpdateCustomerSubscription, { priceId, upgradeInterval, finalize, cancel, toECM, isPreviewMode, productToUpgradeToId });
  };

  const getCustomerData = async () => {
    return fn(CloudFunctions.GetCustomerData);
  };

  const requestCollaboration = async (request: any) => {
    return fn(CloudFunctions.RequestCollaboration, request);
  };

  const getAdventCalendar = async () => {
    return fn(CloudFunctions.GetAdventCalendar);
  };

  const unlockAdventDay = async (day, year) => {
    return fn(CloudFunctions.UnlockAdventDay, { day, year });
  };

  const notifyAdventData = async (day, year, data?) => {
    return fn(CloudFunctions.NotifyAdventData, { day, year, data });
  };

  const trackConversion = async (event, params = {}) => {
    return fn(CloudFunctions.TrackConversion, { event, params });
  };

  const getExperiments = async () => {
    return fn(CloudFunctions.GetExperiments);
  };

  const getAwsGuid = async (guid) => {
    return fn(CloudFunctions.GetAwsGuid, { guid });
  };

  const getEbooks = async (page, isMobile) => {
    return fn(CloudFunctions.GetEbooks, { page, isMobile, language: currentLanguage });
  };

  const getNextQuestion = async (questionId?: string, answerId?: string) => {
    return fn(CloudFunctions.GetNextQuestion, { questionId, answerId });
  };

  const unlockEbook = async (ebookId, value) => {
    return fn(CloudFunctions.UnlockEbook, { ebookId, value });
  };

  const getEbookDownload = async (ebookId) => {
    return fn(CloudFunctions.GetEbookDownload, { ebookId });
  };

  const manageCache = (op: string, params?: string) => {
    return async () => {
      const start = Date.now();
      const data = await fn(CloudFunctions.ManageCache, { op, params });
      return { data, time: Date.now() - start };
    };
  };

  const getCourses = (companyId = '') => {
    return fn(CloudFunctions.GetCourses, { countryProv: country_code, onlyCompanyCourse: !!companyId, companyId: companyId }).then((data) => {
      const { courses } = data;
      setCourses(courses);
    });
  };

  const updateCourse = (
    name: string,
    withCertificate: boolean,
    visibleForAso: boolean,
    description: string[],
    visible: boolean,
    order: number,
    maxErrors: number,
    companyId?: string,
    id?: string
  ) => {
    return fn(CloudFunctions.UpdateCourse, { name, withCertificate, visibleForAso, description, visible, order, maxErrors, companyId, id });
  };

  const requestCourseContent = async (id: string) => {
    return fn(CloudFunctions.RequestCourseContent, { id });
  };

  const queryGoogleDrive = (
    op: 'LISTALL' | 'TOS3',
    options: Partial<{
      pageToken: string;
      folder: string;
      fileId: string;
      fileName: string;
    }> = {}
  ) => {
    return fn(CloudFunctions.GoogleDrive, { op, options }, { useExponentialBackoff: op != 'TOS3' });
  };

  const queryHubspot = (op: 'BLOG_PAGE' | 'BLOG_ARTICLE' | 'BLOG_TAGS', options: Partial<{ after: string; id: string }> = {}) => {
    return fn(CloudFunctions.HubspotWrapper, { op, options });
  };

  const requestTicketRating = (ticketId: string, ticketTitle: string, authorId: string) => {
    return fn(CloudFunctions.RequestTicketRating, { ticketId, ticketTitle, authorId });
  };

  const getUserNotifications = (page: number) => {
    return fn(CloudFunctions.GetUserNotifications, { page });
  };

  const readAllUserNotifications = async () => {
    try {
      // We'll implement an optimistic update for each notification
      setNotifications((previousNotifications) => previousNotifications.map((notification) => ({ ...notification, read: true })));
      // After that we actually call the function to mark all notifications as read on db
      const response = await fn(CloudFunctions.SetReadAllUserNotifications);
      if (response.success) {
        return response;
      }
    } catch (error) {
      // Due to unexpected behavior we have to revert the optimistic update fetching all the notifications from server
      const notifications = await fn(CloudFunctions.GetUserNotifications, { page: 0 });
      setNotifications(notifications);
      console.error('Error while marking all notifications as read:', error);
    }
    return fn(CloudFunctions.SetReadAllUserNotifications);
  };

  const sendVideoNotifications = (notificationId: string) => {
    return fn(CloudFunctions.SendVideoNotifications, { notificationId });
  };

  const sendNotification = (uids: string[], title: string, body: string, dryRun = false) => {
    return fn(CloudFunctions.SendNotification, { uids, title, body, dryRun });
  };

  const getStripeDashboard = () => {
    return fn(CloudFunctions.GetStripeDashboard);
  };

  const submitReservation = (event: string, body: any) => {
    return fn(CloudFunctions.Reservation, { event, body });
  };

  const earlyRenew = (product?: any) => {
    return fn(CloudFunctions.HandleCredit, { countryProv: country_code, language: currentLanguage, product });
  };

  const buyShop = (slug: string) => {
    return fn(CloudFunctions.BuyShop, { countryProv: country_code, language: currentLanguage, slug });
  };

  const getFadCourses = (uid?: string) => {
    return fn(CloudFunctions.GetFadCourses, { userId: uid });
  };

  const getEcmPackages = (uid?: string) => {
    return fn(CloudFunctions.GetEcmPackages, { uid: uid });
  };

  const fadSubscribeToCourses = (courses: any[], fuid: string, fad_id: string, currentCreditBalance: number) => {
    return fn(CloudFunctions.FadSubscribeToCourses, { courses, fuid, fad_id, currentCreditBalance });
  };

  const value: IFirebaseContext = {
    currentUser,
    isLoggedIn,
    userClaims,

    getStaticData,

    userData,
    userDataFmt,
    userCompanyData,
    updateUserData,
    getVideoShardedCounterIncrementor,
    getVideoOpeningsShardedCounterIncrementor,
    updateUserVideoAnalytics,
    updateHubspotProperties,
    getBrevoContactLink,

    plans,
    promoCodes,
    subscription,
    isSubscriptionCheckedOnce,
    globalSecondsIncrementor,

    isTrial,
    trialTimeLeft,
    isTrialExpired,

    getDeviceIdentifier,
    checkDeviceRegistrationAbility,
    registerDevice,
    getRegisteredDevices,
    removeDevice,

    addCheckoutSession,
    getCustomerPortalUrl,

    staticData,
    isFreeWeek: isFreeWeekActive(staticData),
    signupData,
    seoUpdateInProgress,
    seoUpdateLogs,

    notifications,
    notificationBadge,
    addNotifications,
    readNotification,
    cleanNotifications,

    getVideosForTag,
    requestVideoData,
    videos,
    comingSoonVideos,
    mostViewsVideos,
    freeVideos,
    keepWatching,
    newVideos,
    series,
    seriesAuthors,
    seriesTags,
    hasMoreSeriesTags,
    loadMoreSeriesTags,
    loadingMoreSeriesTags,
    courses,
    billboard,
    getCourse,
    getTest,
    validateTest,
    tags,
    authors,
    getSeries,
    isVideoLocked,
    recommendations,
    bonusVideos,
    setValidBonus,

    queryVideosByString,
    getVideoCardsByIds,
    getCachedVideoCardsByIds,

    tagCards,
    myList,
    addToMyList,
    removeFromMyList,
    myListLoading,

    login,
    loginWithGoogle,
    loginWithApple,
    loginWithToken,
    signup,
    logout,
    logoutAll,
    resetPassword,
    updateEmail,
    updatePassword,
    checkEmail,
    sendEmailVerification,
    recoverPassword,
    changePassword,
    reauthenticate,
    reauthenticateGoogle,
    deleteAccount,

    mfaGetSession,
    mfaEnroll,
    mfaUnenroll,

    getTvLoginCode,
    listenForTvLoginToken,
    consumeTvLoginCode,

    getTickets,
    getTicket,
    submitTicket,
    getTicketStatuses,
    rateTicket,
    assignTicket,

    getUserData,
    getUserCourse,
    updateUser,
    requestImpersonationToken,

    getPublishData,
    requestVideoContent,
    getStats,
    getLogs,
    upsertHubspotProduct,
    getVideoReports,
    getFeedbacks,
    getCompanies,
    getCompanyUsers,
    updateCompany,
    getStripeOneOffs,
    getCheckoutDetails,
    createOneOffSession,
    updateCustomerSubscription,
    getCustomerData,
    requestCollaboration,
    getAdventCalendar,
    unlockAdventDay,
    notifyAdventData,
    trackConversion,
    getExperiments,
    getAwsGuid,
    getEbooks,
    getNextQuestion,
    unlockEbook,
    getEbookDownload,
    manageCache,
    getCourses,
    updateCourse,
    requestCourseContent,
    queryGoogleDrive,
    queryHubspot,
    requestTicketRating,
    getUserNotifications,
    readAllUserNotifications,
    sendVideoNotifications,
    sendNotification,
    getStripeDashboard,
    submitReservation,
    earlyRenew,
    buyShop,
    getFadCourses,
    getEcmPackages,
    fadSubscribeToCourses,
    getStreamingData,

    getCustomFlowUsers,
    massiveCustomSignup,
    getCourseStatus,
    handleCustomUser,

    getLegal,
    getActyTutorCategories,
    getActyTutors,
    getActyAppointments,
    getActyAppointment,
    getActyReservationUrl,
    getActyFaq,

    storage,
    videoDataStorage,
    database,
    realtimeDatabase,
    analytics,

    fv,
    timestamp,

    deviceFcmToken,

    setTrack,
    setSocketFire,
  };

  return <FirebaseContext.Provider value={value}>{loading ? <FullscreenLoader /> : children}</FirebaseContext.Provider>;
};
